import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpen, Copy, Check } from "lucide-react";

function buildSnippets(p, gw) {
  const proto = (p.protocol || "http").toLowerCase();
  const socks = proto === "socks5";
  const scheme = socks ? "socks5h" : proto;
  const { host, port, username: u, password: pw } = p;
  const gwHost = window.location.hostname;
  const gwPort = gw?.port || "8888";
  return [
    {
      id: "gateway",
      label: `Gateway :${gwPort}`,
      note: gw?.running
        ? `Your own proxy server — no username/password. Set in your browser/OS: HTTP proxy ${gwHost}:${gwPort}. All traffic automatically exits through the active IP (${p.exit_ip}). The port can be changed in Settings. Only reachable while the app runs on your own computer/VPS.`
        : `Gateway is not running${gw?.error ? `: ${gw.error}` : ""}. Change the port in Settings.`,
      code: `Proxy Address : ${gwHost}\nPort          : ${gwPort}\nAuth          : (not required)\n\ncurl -x http://${gwHost}:${gwPort} https://api.ipify.org`,
    },
    {
      id: "curl",
      label: "curl",
      note: socks ? "socks5h = DNS resolved by the proxy (recommended)." : "Standard HTTP/HTTPS proxy.",
      code: `curl -x ${scheme}://${u}:${pw}@${host}:${port} https://api.ipify.org`,
    },
    {
      id: "windows",
      label: "Windows / macOS",
      note: socks
        ? "Windows Settings only supports HTTP proxy. For SOCKS5 use Proxifier / a browser extension (FoxyProxy), or set it on macOS: System Settings → Network → Proxies → SOCKS Proxy."
        : "Settings → Network & Internet → Proxy → Manual proxy setup. Username & password are requested on first browsing.",
      code: socks
        ? `SOCKS Host : ${host}\nPort       : ${port}\nUsername   : ${u}\nPassword   : ${pw}`
        : `Address  : ${host}\nPort     : ${port}\nUsername : ${u}\nPassword : ${pw}`,
    },
    {
      id: "firefox",
      label: "Firefox",
      note: "Settings → Network Settings → Manual proxy configuration. Check “Proxy DNS when using SOCKS v5”.",
      code: socks
        ? `SOCKS Host : ${host}   Port : ${port}\nSOCKS v5   : ✓\nProxy DNS when using SOCKS v5 : ✓\nLogin      : ${u}\nPassword   : ${pw}`
        : `HTTP Proxy  : ${host}   Port : ${port}\n☑ Also use this proxy for HTTPS\nLogin       : ${u}\nPassword    : ${pw}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      note: socks
        ? "Settings → Advanced → Connection type → Use custom proxy → SOCKS5."
        : "Telegram only supports SOCKS5/MTProto. Rebuild the proxy with the SOCKS5 protocol.",
      code: socks
        ? `tg://socks?server=${host}&port=${port}&user=${encodeURIComponent(u)}&pass=${encodeURIComponent(pw)}`
        : `# build the proxy with Protocol = SOCKS5 first`,
    },
    {
      id: "python",
      label: "Python",
      note: socks ? "pip install requests[socks]" : "pip install requests",
      code: `import requests\n\nproxy = "${scheme}://${u}:${pw}@${host}:${port}"\nr = requests.get("https://api.ipify.org?format=json",\n                 proxies={"http": proxy, "https": proxy}, timeout=30)\nprint(r.json())`,
    },
    {
      id: "env",
      label: "Shell env",
      note: "Works for almost every CLI (git, pip, wget, npm).",
      code: `export HTTP_PROXY="${scheme}://${u}:${pw}@${host}:${port}"\nexport HTTPS_PROXY="${scheme}://${u}:${pw}@${host}:${port}"\nexport ALL_PROXY="${scheme}://${u}:${pw}@${host}:${port}"`,
    },
  ];
}

function CopyBtn({ text, id }) {
  const [ok, setOk] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setOk(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setOk(false), 1500);
  };
  return (
    <Button
      onClick={copy}
      variant="outline"
      size="sm"
      className="rounded-none border-line hover:border-neon hover:text-neon h-7 px-2 font-mono text-xs"
      data-testid={`guide-copy-${id}`}
    >
      {ok ? <Check size={13} className="text-laser" /> : <Copy size={13} />} copy
    </Button>
  );
}

export default function UsageGuide({ active, gateway }) {
  if (!active) {
    return (
      <div className="card-panel p-5 flex items-center gap-3" data-testid="usage-guide-empty">
        <BookOpen size={18} className="text-dim" />
        <div>
          <span className="overline">How to Use</span>
          <p className="text-sm text-dim">Activate a proxy first, and copy-ready config guides will appear here.</p>
        </div>
      </div>
    );
  }

  const snippets = buildSnippets(active, gateway);
  const proto = (active.protocol || "http").toUpperCase();

  return (
    <div className="card-panel" data-testid="usage-guide">
      <div className="p-5 pb-3 border-b border-line flex items-center justify-between">
        <div>
          <span className="overline">Step 3 · Use in other apps</span>
          <h3 className="font-heading text-lg tracking-tight">How to Use · {proto}</h3>
        </div>
        <div className="flex items-center gap-2">
          {gateway && (
            <span className={`font-mono text-xs border px-2 py-1 ${gateway.running ? "border-laser/40 text-laser" : "border-signal/40 text-signal"}`} data-testid="gateway-status-chip">
              :{gateway.port} {gateway.running ? `live · ${gateway.connections} conn` : "off"}
            </span>
          )}
          <span className="font-mono text-xs text-dim border border-line px-2 py-1" data-testid="usage-guide-endpoint">
            {active.host}:{active.port}
          </span>
        </div>
      </div>

      <Tabs defaultValue="gateway" className="p-4">
        <TabsList className="bg-black border border-line rounded-none h-auto flex-wrap justify-start gap-1 p-1">
          {snippets.map((s) => (
            <TabsTrigger
              key={s.id}
              value={s.id}
              className="rounded-none font-mono text-xs data-[state=active]:bg-neon data-[state=active]:text-white"
              data-testid={`guide-tab-${s.id}`}
            >
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {snippets.map((s) => (
          <TabsContent key={s.id} value={s.id} className="mt-3 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs text-dim">{s.note}</p>
              <CopyBtn text={s.code} id={s.id} />
            </div>
            <pre className="bg-black border border-line p-3 font-mono text-[11px] text-foreground whitespace-pre-wrap break-all" data-testid={`guide-code-${s.id}`}>
              {s.code}
            </pre>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
